import * as React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { useStyles } from './TaskSidebar.styles';
import { formatScreamingSnakesToPrettyStrings } from 'task-manager/formatScreamingSnakesToPrettyStrings';
import moment from 'moment';
import { AssigneeType, Job } from 'task-manager/types';
import { Icon } from 'bench-internal-theme';
import useV2Query from 'utils/useV2Query';
import { GET_JOB_HISTORY } from 'task-manager/queries';

type JobHistoryProps = {
  jobId: number;
  taskId: number;
};

const dateFormat = 'MMM D, YYYY, hh:mm:ss a';

export const JobHistory = ({ jobId, taskId }: JobHistoryProps) => {
  const { data, loading } = useV2Query<{ job: Pick<Job, 'id' | 'tasks'> }>(GET_JOB_HISTORY, {
    variables: {
      jobId,
    },
    skip: !jobId,
  });

  const classes = useStyles();

  const historyTasks = React.useMemo(
    () =>
      data?.job?.tasks
        ? data?.job.tasks
            .filter(t => t.id !== taskId)
            .sort((a, b) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime())
        : [],
    [data, taskId],
  );

  return (
    <Box className={classes.topSection}>
      <Accordion square elevation={0}>
        <AccordionSummary expandIcon={<Icon icon="arrowDown" viewBox="0 0 12 6" size="xsmall" />}>
          <Typography variant="h3" className={classes.sectionTitle}>
            Job History
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'grid', gridGap: '16px', width: '100%' }}>
            {historyTasks.length ? (
              historyTasks.map(task => (
                <Box key={task.id}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Tooltip
                      title={`Created On ${moment(new Date(task.createdOn)).format(dateFormat)}`}
                    >
                      <Box className={classes.noteText}>
                        {formatScreamingSnakesToPrettyStrings(task.type)}
                      </Box>
                    </Tooltip>
                    <Tooltip title="Resolution">
                      <Box className={classes.noteText}>
                        {task?.resolution
                          ? formatScreamingSnakesToPrettyStrings(task.resolution)
                          : 'Pending'}
                      </Box>
                    </Tooltip>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Tooltip
                      title={`Assigned On ${
                        task?.assignedOn ? moment(new Date(task.assignedOn)).format(dateFormat) : ''
                      }`}
                    >
                      <Typography className={classes.noteDetail}>
                        {task.assignedPerson?.fullName ?? 'Not Assigned'},{' '}
                        {formatScreamingSnakesToPrettyStrings(AssigneeType[task.assigneeType])}
                      </Typography>
                    </Tooltip>
                    <Tooltip title="Resolved On">
                      <Typography className={classes.noteDetail}>
                        {task.resolvedOn
                          ? moment(new Date(task.resolvedOn)).format(dateFormat)
                          : ''}
                      </Typography>
                    </Tooltip>
                  </Box>
                </Box>
              ))
            ) : loading ? (
              <Typography>Loading...</Typography>
            ) : (
              <Typography>There are no previous tasks for this job.</Typography>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
